import { useState, useEffect } from "react";
import { puedeCrear } from "../auth/permisos";

import { avisarError } from "../../shared/errores";
import { eliminar, listar, rpc } from "../../services/datos";
import { avisar, confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";

// CSV de las respuestas (pregunta, quién respondió y fecha), como lo entregaba la API anterior.
export function csvDeResultados(resultados) {
  const celda = (valor) => {
    const texto = valor === null || valor === undefined ? "" : String(valor);
    return /[",\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
  };
  const filas = [["pregunta_id", "pregunta", "miembro_id", "respuesta", "fecha"]];
  for (const r of resultados) {
    for (const resp of r.respuestas) {
      filas.push([r.pregunta_id, r.pregunta, resp.miembro_id || "anonimo", resp.respuesta, resp.respondido_en]);
    }
  }
  return filas.map((fila) => fila.map(celda).join(",")).join("\n") + "\n";
}

function CrearEncuesta({ onCancelar, onCreada }) {
  const [encuesta, setEncuesta] = useState({
    titulo: "", descripcion: "", dirigida_a: "todos",
    anonima: false, estado: "activa"
  });
  const [preguntas, setPreguntas] = useState([]);

  const agregarPregunta = () => {
    setPreguntas([...preguntas, {
      texto: "", tipo: "texto", opciones: [], requerida: true
    }]);
  };

  const actualizarPregunta = (i, campo, valor) => {
    const nuevas = [...preguntas];
    nuevas[i][campo] = valor;
    setPreguntas(nuevas);
  };

  const agregarOpcion = (i) => {
    const nuevas = [...preguntas];
    nuevas[i].opciones.push("");
    setPreguntas(nuevas);
  };

  const actualizarOpcion = (pi, oi, valor) => {
    const nuevas = [...preguntas];
    nuevas[pi].opciones[oi] = valor;
    setPreguntas(nuevas);
  };

  const eliminarPregunta = (i) => {
    setPreguntas(preguntas.filter((_, idx) => idx !== i));
  };

  const guardar = () => {
    if (!encuesta.titulo) { avisar("El título es obligatorio"); return; }
    if (preguntas.length === 0) { avisar("Agrega al menos una pregunta"); return; }

    // Se crea todo junto (encuesta, preguntas y opciones) en una sola transacción.
    rpc("crear_encuesta", { p_encuesta: encuesta, p_preguntas: preguntas })
      .then((id) => onCreada(id))
      .catch((e) => avisar(e.message, "error"));
  };

  const input = "campo";
  const label = "etiqueta";

  return (
    <div className="max-w-3xl">
      <h3 className="text-xl font-black uppercase tracking-tight text-white mb-6">Nueva Encuesta</h3>

      <div className="panel p-6 mb-6">
        <h4 className="text-white font-semibold mb-4">Información General</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={label}>Título *</label>
            <input value={encuesta.titulo} onChange={(e) => setEncuesta({ ...encuesta, titulo: e.target.value })} className={input} placeholder="Título de la encuesta" />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Descripción</label>
            <textarea value={encuesta.descripcion} onChange={(e) => setEncuesta({ ...encuesta, descripcion: e.target.value })} className={input} rows={2} placeholder="Descripción opcional" />
          </div>
          <div>
            <label className={label}>Dirigida a</label>
            <Selector value={encuesta.dirigida_a} onChange={(e) => setEncuesta({ ...encuesta, dirigida_a: e.target.value })} className={input}>
              <option value="todos">Todos</option>
              <option value="miembro">Miembros</option>
              <option value="congregante">Congregantes</option>
              <option value="visitante">Visitantes</option>
              <option value="servidor">Servidores</option>
            </Selector>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <input type="checkbox" id="anonima" checked={encuesta.anonima} onChange={(e) => setEncuesta({ ...encuesta, anonima: e.target.checked })} className="w-4 h-4 accent-yellow-500" />
            <label htmlFor="anonima" className="text-white text-sm">Encuesta anónima</label>
          </div>
        </div>
      </div>

      <div className="panel p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-white font-semibold">Preguntas</h4>
          <button onClick={agregarPregunta} className="btn-primario btn-sm">
            Agregar pregunta
          </button>
        </div>

        {preguntas.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-8">No hay preguntas aún. Haz clic en "Agregar pregunta"</p>
        )}

        {preguntas.map((p, i) => (
          <div key={i} className="panel-interior p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-bold text-sm">Pregunta {i + 1}</span>
              <button onClick={() => eliminarPregunta(i)} className="text-red-400 text-sm hover:text-red-300">Eliminar</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className={label}>Texto de la pregunta</label>
                <input value={p.texto} onChange={(e) => actualizarPregunta(i, "texto", e.target.value)} className={input} placeholder="¿Qué quieres preguntar?" />
              </div>
              <div>
                <label className={label}>Tipo</label>
                <Selector value={p.tipo} onChange={(e) => actualizarPregunta(i, "tipo", e.target.value)} className={input}>
                  <option value="texto">Texto libre</option>
                  <option value="opcion_multiple">Opción múltiple</option>
                  <option value="si_no">Sí / No</option>
                  <option value="escala">Escala 1-5</option>
                </Selector>
              </div>
            </div>

            {p.tipo === "opcion_multiple" && (
              <div>
                <label className={label}>Opciones</label>
                {p.opciones.map((o, oi) => (
                  <input key={oi} value={o} onChange={(e) => actualizarOpcion(i, oi, e.target.value)} className={`${input} mb-2`} placeholder={`Opción ${oi + 1}`} />
                ))}
                <button onClick={() => agregarOpcion(i)} className="text-dorado text-sm hover:underline">+ Agregar opción</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button onClick={guardar} className="flex-1 btn-primario">
          Crear Encuesta
        </button>
        <button onClick={onCancelar} className="flex-1 btn-fantasma">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function ResultadosEncuesta({ encuestaId, onVolver }) {
  const [resultados, setResultados] = useState([]);
  const [encuesta, setEncuesta] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    listar("encuestas", { filtros: { id: encuestaId } })
      .then(([data]) => setEncuesta(data || null))
      .catch(avisarError);

    rpc("encuesta_resultados", { p_id: encuestaId })
      .then((data) => { setResultados(data || []); setCargando(false); })
      .catch((e) => { setCargando(false); avisarError(e); });
  }, [encuestaId]);

  const esAnonima = encuesta?.anonima;

  // El CSV se arma con los resultados ya cargados.
  const exportarCSV = async () => {
    try {
      const enlace = document.createElement("a");
      enlace.href = URL.createObjectURL(new Blob([csvDeResultados(resultados)], { type: "text/csv;charset=utf-8" }));
      enlace.download = `encuesta_${encuestaId}.csv`;
      enlace.click();
      URL.revokeObjectURL(enlace.href);
    } catch (error) {
      avisarError(error);
    }
  };

  return (
    <div className="max-w-3xl">
      <button onClick={onVolver} className="text-dorado text-sm mb-4 hover:underline">← Volver</button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-black uppercase tracking-tight text-white">{encuesta?.titulo}</h3>
          {esAnonima ? (
            <p className="text-gray-400 text-xs mt-1">Encuesta anónima – no se muestra la identidad de los encuestados.</p>
          ) : (
            <p className="text-gray-400 text-xs mt-1">Encuesta identificada – se muestra quién respondió.</p>
          )}
        </div>
        <button onClick={exportarCSV} className="btn-exito btn-sm">
          Exportar CSV
        </button>
      </div>

      {cargando ? (
        <Esqueleto texto="Cargando resultados…" />
      ) : resultados.length === 0 ? (
        <p className="text-gray-400">No hay respuestas aún</p>
      ) : (
        resultados.map((r, i) => (
          <div key={i} className="panel p-6 mb-4">
            <p className="text-white font-semibold mb-3">{i + 1}. {r.pregunta}</p>
            <p className="text-gray-400 text-xs mb-3">{r.respuestas.length} respuestas</p>

            {r.tipo === "si_no" && (
              <div className="flex gap-4">
                <div className="border border-emerald-500/30 bg-emerald-500/15 text-emerald-300 rounded-xl px-4 py-2 text-sm">
                  Sí: {r.respuestas.filter(x => x.respuesta === "si").length}
                </div>
                <div className="border border-rose-500/30 bg-rose-500/15 text-rose-300 rounded-xl px-4 py-2 text-sm">
                  No: {r.respuestas.filter(x => x.respuesta === "no").length}
                </div>
              </div>
            )}

            {r.tipo === "opcion_multiple" && (
              <div className="space-y-2">
                {[...new Set(r.respuestas.map((x) => x.respuesta))].map((opcion, j) => {
                  const count = r.respuestas.filter(x => x.respuesta === opcion).length;
                  const pct = Math.round((count / r.respuestas.length) * 100);
                  return (
                    <div key={j}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white">{opcion}</span>
                        <span className="text-dorado">{count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-oscuro rounded-full h-2">
                        <div className="bg-dorado h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {r.tipo === "escala" && (
              <div className="flex gap-2">
                {[1,2,3,4,5].map(n => (
                  <div key={n} className="bg-oscuro rounded-lg p-3 text-center flex-1 border border-gray-700">
                    <p className="text-white font-bold">{n}</p>
                    <p className="text-gray-400 text-xs">{r.respuestas.filter(x => x.respuesta === String(n)).length}</p>
                  </div>
                ))}
              </div>
            )}

            {r.tipo === "texto" && (
              <div className="space-y-3 max-h-56 overflow-y-auto">
                {r.respuestas.map((resp, j) => (
                  <div key={j} className="bg-oscuro rounded-lg p-3 border border-gray-700">
                    <p className="text-white text-sm">{resp.respuesta}</p>
                    {esAnonima ? (
                      <p className="text-gray-400 text-xs mt-2">Respondido por: Anónimo</p>
                    ) : resp.nombres ? (
                      <p className="text-gray-400 text-xs mt-2">Respondido por: {resp.nombres} {resp.apellidos}</p>
                    ) : (
                      <p className="text-gray-400 text-xs mt-2">Respondido por: Anónimo</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {r.tipo !== "texto" && (
              <div className="mt-4">
                <p className="text-gray-300 text-sm mb-2">Respuestas individuales</p>
                <div className="space-y-3 max-h-56 overflow-y-auto">
                  {r.respuestas.map((resp, j) => (
                    <div key={j} className="bg-oscuro rounded-lg p-3 border border-gray-700">
                      <p className="text-white text-sm">{resp.respuesta}</p>
                      {esAnonima ? (
                        <p className="text-gray-400 text-xs mt-2">Respondido por: Anónimo</p>
                      ) : resp.nombres ? (
                        <p className="text-gray-400 text-xs mt-2">Respondido por: {resp.nombres} {resp.apellidos}</p>
                      ) : (
                        <p className="text-gray-400 text-xs mt-2">Respondido por: Anónimo</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function Encuestas({ usuario }) {
  const [encuestas, setEncuestas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useState("lista");
  const [encuestaSeleccionada, setEncuestaSeleccionada] = useState(null);

  const cargarEncuestas = () => {
    listar("encuestas", { orden: "creado_en", descendente: true })
      .then((data) => { setEncuestas(data); setCargando(false); })
      .catch((e) => { setCargando(false); avisarError(e); });
  };

  useEffect(() => { cargarEncuestas(); }, []);

  const copiarLink = (id) => {
    const link = `${window.location.origin}/encuesta/${id}`;
    navigator.clipboard.writeText(link);
    avisar("Link copiado al portapapeles", "exito");
  };

  if (vista === "crear") {
    return (
      <div className="p-6">
        <CrearEncuesta
          onCancelar={() => setVista("lista")}
          onCreada={(id) => { cargarEncuestas(); setVista("lista"); }}
        />
      </div>
    );
  }

  if (vista === "resultados" && encuestaSeleccionada) {
    return (
      <div className="p-6">
        <ResultadosEncuesta
          encuestaId={encuestaSeleccionada}
          onVolver={() => setVista("lista")}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="titulo-pagina">Encuestas</h2>
          <p className="text-gray-400 text-sm mt-1">{encuestas.length} encuestas creadas</p>
        </div>
        {puedeCrear(usuario) ? (
          <button
            onClick={() => setVista("crear")}
            className="btn-primario"
          >
            Nueva Encuesta
          </button>
        ) : (
          <span className="text-sm text-gray-400">Solo pastor o administrador puede crear</span>
        )}
      </div>

      {cargando ? (
        <Esqueleto tipo="tarjetas" texto="Cargando encuestas…" />
      ) : encuestas.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg">No hay encuestas creadas aún</p>
          <p className="text-gray-600 text-sm mt-2">Haz clic en "Nueva Encuesta" para crear la primera</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {encuestas.map((e) => (
            <div key={e.id} className="panel p-5 hover:border-dorado transition">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-white font-bold">{e.titulo}</h3>
                <span className={`insignia ${e.estado === "activa" ? "border border-emerald-500/30 bg-emerald-500/15 text-emerald-300" : "border border-zinc-500/30 bg-zinc-500/15 text-zinc-400"}`}>
                  {e.estado}
                </span>
              </div>
              {e.descripcion && <p className="text-gray-400 text-sm mb-3">{e.descripcion}</p>}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs bg-oscuro px-2 py-1 rounded-full text-gray-400">
                  {e.anonima ? "Anónima" : "Identificada"}
                </span>
                <span className="text-xs bg-oscuro px-2 py-1 rounded-full text-gray-400">
                  Dirigida a: {e.dirigida_a}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setEncuestaSeleccionada(e.id); setVista("resultados"); }}
                  className="flex-1 btn-fantasma btn-sm"
                >
                  Resultados
                </button>
                <button
                  onClick={() => copiarLink(e.id)}
                  className="flex-1 btn-fantasma btn-sm"
                >
                  Copiar Link
                </button>
                <button
                  onClick={async () => {
                    if (!(await confirmar("¿Seguro que quieres eliminar esta encuesta?", { aceptar: "Eliminar" }))) return;
                    eliminar("encuestas", e.id)
                      .then(() => cargarEncuestas())
                      .catch(avisarError);
                  }}
                  className="flex-1 border border-red-500 text-red-400 py-2 rounded-lg text-sm hover:bg-red-500 hover:text-white transition"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Encuestas;